/**
 * Error monitoring — the one place that decides where a crash goes.
 *
 * Before this existed, `ErrorBoundary` caught a crash, showed the fallback, and
 * logged to a console nobody was watching. The reporting channel was a parent
 * messaging us on WhatsApp, which meant we learned about problems late,
 * secondhand, and only the ones somebody bothered to mention.
 *
 * Two deliberate properties:
 *
 *   • **Lazily loaded.** Sentry is imported dynamically so it lands in its own
 *     chunk instead of the main bundle. The public portfolio pages are opened
 *     by parents on mobile data from a WhatsApp link, and the first-load budget
 *     there is already tight — monitoring must not be paid for on paint.
 *
 *   • **Inert without a DSN.** No `REACT_APP_SENTRY_DSN` means every function
 *     here is a no-op, so local dev and any build made before the Sentry
 *     project exists behave exactly as they did. Nothing here may ever throw:
 *     the reporting path failing must not become the thing that breaks the app.
 *
 * Reports captured before the SDK finishes loading are buffered and flushed, so
 * a crash during the first seconds after boot — the most likely moment — is not
 * the one report that gets lost.
 */
import { env } from 'config';

/** Reports captured before the SDK is ready. Bounded: a crash loop must not grow it forever. */
const pending = [];
const MAX_PENDING = 25;

let sentry = null;
let loading = null;

const enabled = () => Boolean(env.sentryDsn);

/**
 * Load and configure the SDK exactly once. Returns null when monitoring is off
 * or the SDK could not be loaded, and never rejects.
 */
function load() {
  if (!enabled()) return Promise.resolve(null);
  if (loading) return loading;

  loading = import('@sentry/react')
    .then((mod) => {
      mod.init({
        dsn: env.sentryDsn,
        environment: env.appEnv,
        // Errors only. Performance tracing and session replay are separate
        // budgets (bandwidth and quota) — turn them on deliberately, not by
        // inheriting a default.
        tracesSampleRate: 0,
        // Firestore's own retries surface as noisy transient failures that
        // resolve themselves; they drown the reports that need a human.
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          'Non-Error promise rejection captured',
          /Failed to get document because the client is offline/i,
        ],
      });
      sentry = mod;
      flush();
      return mod;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('[monitoring] could not load error reporting:', error?.message);
      return null;
    });

  return loading;
}

/** Hand every buffered report to the loaded SDK. */
function flush() {
  if (!sentry) return;
  while (pending.length) {
    const { error, context } = pending.shift();
    send(error, context);
  }
}

function send(error, context) {
  try {
    sentry.captureException(error, context ? { extra: context } : undefined);
  } catch {
    /* reporting must never throw into the caller */
  }
}

/**
 * Start monitoring and attach the two global listeners React can't see:
 * unhandled promise rejections (most of our async Firebase calls) and errors
 * raised outside a render, both of which bypass ErrorBoundary entirely.
 *
 * Safe to call once at boot; a second call is a no-op.
 */
export function initMonitoring() {
  if (!enabled()) return;
  load();

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    captureError(reason instanceof Error ? reason : new Error(String(reason)), {
      kind: 'unhandledrejection',
    });
  });

  window.addEventListener('error', (event) => {
    if (event?.error instanceof Error) captureError(event.error, { kind: 'window.onerror' });
  });
}

/**
 * Report an error. Buffers until the SDK is ready, then sends.
 *
 * @param {Error}  error
 * @param {object} [context]  Extra detail attached to the report.
 */
export function captureError(error, context) {
  if (!enabled() || !error) return;

  if (sentry) {
    send(error, context);
    return;
  }

  if (pending.length < MAX_PENDING) pending.push({ error, context });
  load();
}

/**
 * Tag reports with who hit them, so a bug can be read as "every mentor" rather
 * than "someone". Called by AuthContext as the session resolves; passing null
 * on sign-out clears it.
 *
 * Identity is limited to uid and role deliberately — enough to see the shape of
 * an incident, without shipping names or email addresses to a third party.
 */
export function identifyUser(user) {
  if (!enabled()) return;
  load().then((mod) => {
    if (!mod) return;
    try {
      mod.setUser(user ? { id: user.uid, role: user.role || 'unknown' } : null);
    } catch {
      /* non-fatal */
    }
  });
}
